/**
 * Utility functions for handling static file URLs
 * Hardcoded to always use /static/ path in GCS URLs
 */

/**
 * Get the static URL base path - always uses /static/ (not region)
 * Format: https://storage.googleapis.com/{bucket}/static/
 */
export const getStaticUrl = () => {
  if (typeof window !== 'undefined' && window.DJANGO_CONTEXT?.staticUrl) {
    const staticUrl = window.DJANGO_CONTEXT.staticUrl;
    
    // Extract bucket name from URL (before /static/ or /asia-southeast1/)
    const bucketMatch = staticUrl.match(/https?:\/\/storage\.googleapis\.com\/([^\/]+)/);
    if (bucketMatch) {
      const bucketName = bucketMatch[1];
      // Hardcode /static/ path - never use region
      return `https://storage.googleapis.com/${bucketName}/static/`;
    }
    
    // If not GCS URL, use as-is but ensure trailing slash
    return staticUrl.endsWith('/') ? staticUrl : `${staticUrl}/`;
  }
  
  // Fallback for local development
  return '/static/';
};

/**
 * Get the full URL for a static file
 * @param {string} path - Path to the static file (e.g., 'topbar/logo_ed.png')
 * @returns {string} Full URL to the static file
 */
export const getStaticFileUrl = (path) => {
  const staticUrl = getStaticUrl();
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${staticUrl}${cleanPath}`;
};

/**
 * Resolve URL for channel/PMS image so it loads from the same origin as the app.
 * - If channelImagePath is null/empty, returns PMS default image URL.
 * - If channelImagePath is absolute (http...), uses current origin + pathname so
 *   images work when API returns a different port (e.g. backend 8080, frontend 3000).
 * @param {string|null|undefined} channelImagePath - From API (e.g. channel_image_path)
 * @returns {string} URL suitable for <img src>
 */
const OTA_NAME_TO_FILE = [
  ['booking.com', 'booking.png'],
  ['booking', 'booking.png'],
  ['makemytrip', 'makemytrip.png'],
  ['mmt', 'makemytrip.png'],
  ['goibibo', 'goibibo.png'],
  ['agoda', 'agoda.png'],
  ['expedia', 'expedia.png'],
  ['airbnb', 'airbnb.png'],
  ['easemytrip', 'easymytrip.png'],
  ['cleartrip', 'cleartrip.png'],
];

/** Map channel display name to a known static/ota/*.png asset. */
export function guessOtaStaticPath(channelName) {
  const normalized = String(channelName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!normalized) return null;
  for (const [needle, filename] of OTA_NAME_TO_FILE) {
    const key = needle.replace(/[^a-z0-9]/g, '');
    if (normalized.includes(key)) {
      return `ota/${filename}`;
    }
  }
  return null;
}

export const getChannelImageUrl = (channelImagePath) => {
  if (channelImagePath && channelImagePath.trim()) {
    try {
      let path = channelImagePath.trim();
      // Normalize common DB mistakes (e.g. booking.com.png vs booking.png).
      path = path.replace(/booking\.com\.png/i, 'booking.png');

      if (path.startsWith('http://') || path.startsWith('https://')) {
        const staticMarker = '/static/';
        const markerIdx = path.indexOf(staticMarker);
        if (markerIdx >= 0) {
          const staticPath = path.slice(markerIdx + staticMarker.length);
          return getStaticFileUrl(staticPath || 'ota/pms.png');
        }
        try {
          const u = new URL(path);
          const staticPath = u.pathname.replace(/^\//, '').replace(/^static\//, '');
          return getStaticFileUrl(staticPath || 'ota/pms.png');
        } catch (_) {
          return getStaticFileUrl('ota/pms.png');
        }
      }

      const cleanPath = path.replace(/^\//, '').replace(/^static\//, '');
      return getStaticFileUrl(cleanPath || 'ota/pms.png');
    } catch (_) {
      return getStaticFileUrl('ota/pms.png');
    }
  }
  return getStaticFileUrl('ota/pms.png');
};

/**
 * Resolve channel/PMS logo URL for UI components.
 * @param {{ name?: string, image_path?: string }|null|undefined} channel
 * @param {{ isPMS?: boolean }} options
 */
export function resolveChannelImageUrl(channel, options = {}) {
  const { isPMS = false } = options;
  if (isPMS || channel?.id === 'pms') {
    return getStaticFileUrl('ota/pms.png');
  }

  const imagePath = channel?.image_path;
  if (imagePath && String(imagePath).trim()) {
    return getChannelImageUrl(imagePath);
  }

  const guessed = guessOtaStaticPath(channel?.name);
  if (guessed) {
    return getStaticFileUrl(guessed);
  }

  return null;
}

/**
 * Avatar badge logo for room / booking cards — OTA artwork when available, else PMS for direct bookings.
 * @param {Object|null|undefined} booking
 */
export function resolveRoomBookingChannelLogoSrc(booking) {
  if (!booking) return null;

  const imagePath = booking.channel_image_path || booking.channel_data?.image_path;
  const channelName = booking.channel_name || booking.channel_data?.name;

  const otaSrc = resolveChannelImageUrl({
    image_path: imagePath,
    name: channelName,
  });

  if (otaSrc) {
    return otaSrc;
  }

  return getStaticFileUrl('ota/pms.png');
}

